import { useEffect, useMemo, useState } from "react";
import { Alert, Button, Col, Form, Modal, Row } from "react-bootstrap";
import { useDispatch } from "react-redux";
import i18n from "../../../../i18n";
import { addProjectMember, updateProjectMember } from "../../../../redux/slices/projectsSlice";
import AutocompleteSelect from "../../../autocomplete/AutocompleteSelect/AutocompleteSelect";

const INITIAL_MEMBER_FORM = {
  user_id: "",
  label: "",
  email: "",
  avatar: "",
  role: "",
  start_date: "",
  end_date: "",
  monthly_etis: "",
  status: "Active",
};

const toInputDate = (value) => {
  if (!value) {
    return "";
  }

  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  return parsed.toISOString().slice(0, 10);
};

const normalizeMember = (member) => {
  if (!member) {
    return INITIAL_MEMBER_FORM;
  }

  const fallbackName = `${member?.user?.first_name || ""} ${member?.user?.last_name || ""}`.trim();

  return {
    user_id: String(member?.user_id || member?.value || member?.id || ""),
    label: member?.label || fallbackName,
    email: member?.email || member?.user?.email || "",
    avatar: member?.avatar || member?.user?.profile_picture_url || "",
    role: member?.role || "",
    start_date: toInputDate(member?.start_date),
    end_date: toInputDate(member?.end_date),
    monthly_etis: member?.monthly_etis ?? "",
    status: member?.status || "Active",
  };
};

const AddProjectMembersModal = ({
  project,
  users,
  showModal,
  setShowModal,
  editProjectMember,
  onSaved,
  isCreate
}) => {
  const dispatch = useDispatch();

  const [formData, setFormData] = useState(INITIAL_MEMBER_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [validationError, setValidationError] = useState("");

  useEffect(() => {
    if (showModal) {
      setFormData(normalizeMember(editProjectMember));
      setValidationError("");
      setIsSubmitting(false);
    }
  }, [showModal, editProjectMember]);

  const selectedMember = useMemo(() => {
    if (!formData.user_id) {
      return null;
    }

    return {
      value: formData.user_id,
      label: formData.label,
      email: formData.email,
      avatar: formData.avatar,
    };
  }, [formData.user_id, formData.label, formData.email, formData.avatar]);

  const onMemberSelect = (member) => {
    if (!member) {
      setFormData((previous) => ({
        ...previous,
        user_id: "",
        label: "",
        email: "",
        avatar: "",
      }));
      return;
    }

    setFormData((previous) => ({
      ...previous,
      user_id: String(member.value),
      label: member.label,
      email: member.email,
      avatar: member.avatar,
    }));
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setFormData((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleClose = () => {
    if (isSubmitting) {
      return;
    }

    setShowModal(false);
    setFormData(INITIAL_MEMBER_FORM);
    setValidationError("");
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    if (!project?.id) {
      setValidationError("Project is required.");
      return;
    }

    if (!formData.user_id) {
      setValidationError(i18n.t("components.modal.users.member_required"));
      return;
    }

    if (formData.start_date && formData.end_date && formData.end_date < formData.start_date) {
      setValidationError(i18n.t("components.modal.users.invalid_period"));
      return;
    }

    if (formData.monthly_etis < 0.0 || formData.monthly_etis > 1.0) {
      setValidationError(i18n.t("components.modal.users.invalid_monthly_etis"));
      return;
    }

    setValidationError("");
    setIsSubmitting(true);

    const memberPayload = {
      user_id: !isCreate ? editProjectMember.user_id :
        /^\d+$/.test(String(formData.user_id))
          ? Number(formData.user_id)
          : formData.user_id,
      project_id: !isCreate ? editProjectMember.project_id : project.id,
      start_date: formData.start_date || null,
      end_date: formData.end_date || null,
      monthly_etis: formData.monthly_etis === "" ? null : Number(formData.monthly_etis),
      status: formData.status || "Active",
      role: formData.role || null,
    };

    let result;
    if (isCreate) {
      result = await dispatch(
        addProjectMember({
          projectId: project.id,
          projectMember: memberPayload,
        })
      );
    } else {
      result = await dispatch(
        updateProjectMember({
          projectId: editProjectMember.project_id,
          userId: editProjectMember.user_id,
          projectMember: memberPayload,
        })
      );
    }

    setIsSubmitting(false);

    if (!result.error) {
      await onSaved?.();
      handleClose();
    }
  };

  return (
    <Modal show={showModal} onHide={handleClose} centered>
      <Modal.Header closeButton>
        <Modal.Title>
          {!isCreate
            ? i18n.t("common.actions.update")
            : i18n.t("components.modal.users.add_members_title")}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>

        {isCreate && (
          <div>
            <h6 className="mb-2">{i18n.t("components.modal.users.search_user")}</h6>
            <AutocompleteSelect
              type="users"
              data={users}
              onSelect={onMemberSelect}
              value={selectedMember}
              placeholder={i18n.t("components.modal.users.search_user")}
            />
          </div>
        )}

        {formData.user_id && (
          <div className="project-member-selected-card mt-3">
            <strong className="text-dark">{formData.label}</strong>
            <span>{formData.email}</span>
          </div>
        )}

        <Form onSubmit={handleSubmit} className="mt-3">
          <Row>
            <Col xs={12}>
              <Form.Group className="mb-3">
                <Form.Label>{i18n.t("fields.ProjectMember.role")}</Form.Label>
                <Form.Control
                  name="role"
                  required
                  placeholder={i18n.t("fields.ProjectMember.role_placeholder")}
                  type="text"
                  value={formData.role}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>

            <Col xs={12} md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{i18n.t("fields.ProjectMember.start_date")}</Form.Label>
                <Form.Control
                  name="start_date"
                  type="date"
                  value={formData.start_date}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>

            <Col xs={12} md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{i18n.t("fields.ProjectMember.end_date")}</Form.Label>
                <Form.Control
                  name="end_date"
                  type="date"
                  value={formData.end_date}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>

            <Col xs={12} md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{i18n.t("fields.ProjectMember.monthly_etis")}</Form.Label>
                <Form.Control
                  name="monthly_etis"
                  placeholder={i18n.t("fields.ProjectMember.monthly_etis_placeholder")}
                  type="number"
                  min="0"
                  step="0.01"
                  value={formData.monthly_etis}
                  onChange={handleInputChange}
                />
              </Form.Group>
            </Col>

            <Col xs={12} md={6}>
              <Form.Group className="mb-3">
                <Form.Label>{i18n.t("fields.ProjectMember.status")}</Form.Label>
                <Form.Select name="status" value={formData.status} onChange={handleInputChange}>
                  <option value="Active">{i18n.t("fields.ProjectMember.status_enum.active")}</option>
                  <option value="Inactive">{i18n.t("fields.ProjectMember.status_enum.inactive")}</option>
                  <option value="Ended">{i18n.t("fields.ProjectMember.status_enum.ended")}</option>
                  <option value="Archived">{i18n.t("fields.ProjectMember.status_enum.archived")}</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>

          {validationError && <Alert variant="danger" className="py-2">{validationError}</Alert>}

          <div className="d-flex justify-content-end gap-2 mt-2">
            <Button variant="outline-secondary" onClick={handleClose} disabled={isSubmitting}>
              {i18n.t("common.actions.cancel")}
            </Button>
            <Button variant="primary" type="submit" disabled={isSubmitting}>
              {isSubmitting
                ? "Saving..."
                : !isCreate
                  ? i18n.t("common.actions.update")
                  : i18n.t("common.actions.create")}
            </Button>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default AddProjectMembersModal;
